import {
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useMatch, useParams, useSearchParams } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import equal from 'fast-deep-equal';

import { CfsApi, DataApi, MasterNameApi } from '@/api';
import { IcInfoCircle, IcTriangleWarning } from '@/assets/images';
import {
  AddressNotesModal,
  AddressPreplanModal,
  CFSAddressJurisdictionModal,
  CFSHistoryModal,
  FormCard,
  FormRoot,
  FormValueType,
  RenderFormField,
  ReporterForm,
  SelectItem,
} from '@/components';
import { CallerInformation } from '@/components/CustomForms/ReporterForm/CallerInformationForm';
import { SEVERITY_STATUS } from '@/constants';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import { useRapidSos } from '@/hooks/useRapidSos';
import {
  AssignedUnitShiftStatus,
  IAddress,
  IAddressForm,
  IAgency,
  CFSEvent,
  ICFS,
  ICFSForm,
  MasterNameType,
  TSeverity,
} from '@/models';
import { CFSAddressChangeModal } from '@/pages/CFS/Edit/components/CFSAddressChangeModal';
import { CFSAgencyUnassignModal } from '@/pages/CFS/Edit/components/CFSAgencyUnassignModal';
import { CFSIncidentCodeUnassignModal } from '@/pages/CFS/Edit/components/CFSIncidentCodeUnassignModal';
import {
  getAddressCommonString,
  getAddressInfoLocation,
  isCFSViewOnly,
} from '@/services';
import {
  useAgencyStore,
  useDataStore,
  useToastStore,
  useUserStore,
} from '@/store';
import { colors } from '@/theme/variables';
import {
  compositeObject,
  getMasterNamePhoneNumbersByPriority,
  isInsideMultiPolygon,
} from '@/utils';

import { cfsDefaultValues, CFSFormFields, OnBlurFormFields } from '../data';

export type OptionType = {
  label: string;
  value: string;
  disabled?: boolean;
};

type AddressInfoFormState = ICFSForm['addressInfo'] & {
  address?: IAddressForm | '';
  locationPoints?: any[];
};

export const CFSForm = (props: {
  cfsId?: string;
  hideReporterInfo?: boolean;
}) => {
  const { agencies } = useAgencyStore();
  const { getValues, setValue, trigger, watch } =
    useFormContext<Partial<ICFSForm>>();
  const { cfs, mapBounds, ablyCfsChannel, updateCFS, fetchCFS } =
    useCFSContext();
  const { fetchAssignedUnitShifts } = useUnitShiftContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = useParams();
  const isBaseCfsRoute = Boolean(useMatch({ path: '/cfs/:cfsId', end: true }));
  const checkAddress = searchParams.get('checkAddress');
  const cfsId = props?.cfsId || String(urlParams.cfsId);
  const { updateToast } = useToastStore();
  const { account, user } = useUserStore();
  // CFS (View Only) users see all fields but cannot edit; neutralize every write path.
  const readOnly = isCFSViewOnly(user);

  const { incidentCodes, updateData } = useDataStore();
  const [openAddressBoundaryModal, setOpenAddressBoundaryModal] =
    useState(false);
  const [openAgencyUnassignModal, setOpenAgencyUnassignModal] = useState(false);
  const [pendingRemovedAgencies, setPendingRemovedAgencies] = useState<
    IAgency[]
  >([]);
  const [openIncidentCodeUnassignModal, setOpenIncidentCodeUnassignModal] =
    useState(false);
  const [pendingAddress, setPendingAddress] = useState<
    IAddressForm | null | undefined
  >(undefined);
  const [openAddressChangeModal, setOpenAddressChangeModal] = useState(false);
  const [isAddressMutationPending, setIsAddressMutationPending] =
    useState(false);
  const [suppressIncomingAddressSync, setSuppressIncomingAddressSync] =
    useState(false);
  const previousAddressInfoSnapshotRef = useRef<
    AddressInfoFormState | undefined
  >(undefined);

  const [removingIncidentCode, setRemovingIncidentCode] = useState<
    string | undefined
  >('');
  const [removingAgency, setRemovingAgency] = useState<string | undefined>('');

  const { locations } = useRapidSos(cfs);

  const formAddressValue = watch('addressInfo.address');
  const addressId = formAddressValue?._id;
  const [organizationName, setOrganizationName] = useState<string>('');
  const [timelineCreatedTime, setTimelineCreatedTime] = useState<
    number | string | Date | null
  >(null);

  const fetchTimelineCreatedTime = useCallback(async () => {
    if (!cfsId) return;

    try {
      const res = await CfsApi.getCfsHistory(cfsId, {
        limit: 1000,
        sort: JSON.stringify({ createdAt: -1 }),
        filter: JSON.stringify({
          event: { $in: [CFSEvent.CREATED] },
        }),
      });

      const createdLog = (res.data?.results || []).find((log) => {
        if (log.event !== CFSEvent.CREATED) return false;
        if (log.overriddenAt === null) return false;
        return Boolean(log.createdAt) || typeof log.overriddenAt === 'number';
      });

      if (!createdLog) {
        setTimelineCreatedTime(null);
        return;
      }

      if (
        typeof createdLog.overriddenAt === 'number' &&
        !Number.isNaN(createdLog.overriddenAt)
      ) {
        setTimelineCreatedTime(createdLog.overriddenAt);
        return;
      }

      setTimelineCreatedTime(createdLog.createdAt || null);
    } catch {
      setTimelineCreatedTime(null);
    }
  }, [cfsId]);

  const isNotJurisdictionAddress =
    account?.settings.areas &&
    cfs?.addressInfo?.address &&
    !isInsideMultiPolygon(
      cfs?.addressInfo?.address?.point,
      account?.settings.areas,
    );

  useEffect(() => {
    if (cfs?._id && checkAddress && isNotJurisdictionAddress) {
      return setOpenAddressBoundaryModal(true);
    }
  }, [checkAddress, cfs?._id]);

  // Update CFS form values when CFS data is updated by ably message.
  useEffect(() => {
    if (cfs) {
      const formValues = getValues();
      const { primaryPhoneNumber } = getMasterNamePhoneNumbersByPriority(
        cfs.primaryReporter,
      );
      const _cfs = {
        ...cfs,
        incidentCodes: cfs.incidentCodes.map((code) =>
          String(code._id ?? code),
        ),
        reporter: {
          ...cfs.reporter,
          contactPhoneNumber:
            primaryPhoneNumber?.inputValue ||
            cfs.reporter?.contactPhoneNumber?.replace('+1', ''),
        },
      };
      Object.entries(formValues).forEach(([key, value]) => {
        const incomingValue = _cfs[key as keyof ICFS];

        // Avoid destructive sync when incoming snapshot is transient/incomplete.
        if (incomingValue === undefined || incomingValue === null) {
          return;
        }

        if (
          key === 'requiredUnits' &&
          Array.isArray(incomingValue) &&
          Array.isArray(value) &&
          incomingValue.length === 0 &&
          value.length > 0
        ) {
          return;
        }

        if (!equal(incomingValue, value)) {
          if (key === 'addressInfo') {
            if (suppressIncomingAddressSync) {
              return;
            }
            const incomingAddressInfo = _cfs.addressInfo || {};
            const incomingAddress = _cfs.addressInfo?.address;
            const incomingInstructions = _cfs.addressInfo?.instructions;
            const fallbackLocationPoint =
              _cfs.addressInfo?.locationPoints?.find((lp) => lp.isPrimary) ??
              _cfs.addressInfo?.locationPoints?.[0];
            const fallbackAddressLabel = getAddressInfoLocation(
              _cfs.addressInfo,
            );
            const hasIncomingAddressField =
              Object.prototype.hasOwnProperty.call(
                incomingAddressInfo,
                'address',
              );
            if (
              incomingAddress === undefined &&
              incomingInstructions === undefined &&
              !fallbackLocationPoint &&
              !hasIncomingAddressField
            ) {
              return;
            }
            if (hasIncomingAddressField && incomingAddress) {
              // Carry the per-CFS business name onto the form's address value
              // so the input renders "Business - Street".
              setValue('addressInfo.address', {
                ...incomingAddress,
                businessName:
                  incomingAddressInfo.businessName ??
                  incomingAddress.businessName,
              } as any);
            } else if (fallbackLocationPoint) {
              // Pin-only location (address is null): show the location point
              // as a pseudo-address instead of blanking the field.
              setValue('addressInfo.address', {
                addressLabel: fallbackAddressLabel || undefined,
                normalized: fallbackAddressLabel || undefined,
                point: fallbackLocationPoint.point,
              } as IAddressForm);
            } else if (hasIncomingAddressField) {
              setValue('addressInfo.address', incomingAddress as any);
            }
            if (incomingInstructions !== undefined) {
              setValue('addressInfo.instructions', incomingInstructions);
            }
            return;
          }
          if (key === 'reporter') {
            const currentReporter = value as ICFSForm['reporter'] | undefined;
            const incomingReporter = _cfs.reporter;
            const incomingContactPhoneNumber =
              incomingReporter?.contactPhoneNumber;

            // Preserve the already-derived reporter phone in the form while
            // async CFS snapshots catch up after reporter selection changes.
            if (
              _cfs.primaryReporter &&
              currentReporter?.contactPhoneNumber &&
              !incomingContactPhoneNumber
            ) {
              setValue(
                key as keyof Partial<ICFSForm>,
                {
                  ...incomingReporter,
                  contactPhoneNumber: currentReporter.contactPhoneNumber,
                } as any,
              );
              return;
            }
          }
          setValue(
            key as keyof Partial<ICFSForm>,
            _cfs[key as keyof ICFS] as any,
          );
        }
      });
    }
  }, [JSON.stringify(cfs), suppressIncomingAddressSync]);

  const agencyOptions = useMemo(() => {
    return agencies.map((agency) => ({
      label: agency.name,
      value: agency._id ?? '',
    }));
  }, [agencies]);

  const incidentCodeOptions = useMemo(() => {
    const formatIncidentCode = (code: string, description: string) => {
      if (code && description) {
        return `${code} - ${description}`;
      }
      return code || description || '';
    };
    return incidentCodes.map(({ _id, code, description }) => ({
      label: formatIncidentCode(code ?? '', description ?? ''),
      value: _id ?? '',
    }));
  }, [incidentCodes]);

  const severityOptions = useMemo<SelectItem[]>(() => {
    const severityConfig = [
      { label: 'High', value: TSeverity.HIGH },
      { label: 'Medium', value: TSeverity.MEDIUM },
      { label: 'Low', value: TSeverity.LOW },
    ];

    return severityConfig.map((severity) => ({
      value: severity.value,
      label: (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SEVERITY_STATUS[severity.value],
              border:
                severity.value === TSeverity.MEDIUM
                  ? `1px solid ${colors.grey[30]}`
                  : 'none',
            }}
          />
          <Typography component="span">{severity.label}</Typography>
        </Stack>
      ),
    }));
  }, []);

  const getAddressInfoSnapshot = useCallback((): AddressInfoFormState => {
    const currentAddressInfo = getValues('addressInfo');

    return {
      ...currentAddressInfo,
      address: ((currentAddressInfo as any)?.address ?? '') as
        | IAddressForm
        | '',
      locationPoints: (currentAddressInfo as any)?.locationPoints,
    };
  }, [getValues]);

  const applyFormAddressInfo = useCallback(
    (nextAddressInfo?: AddressInfoFormState) => {
      const currentAddressInfo = getValues('addressInfo');

      setValue('addressInfo', {
        ...currentAddressInfo,
        ...nextAddressInfo,
        address: (nextAddressInfo?.address ?? '') as IAddressForm | '',
        locationPoints: nextAddressInfo?.locationPoints,
      } as any);
    },
    [getValues, setValue],
  );

  const stageAddressInfoMutation = useCallback(
    (address: IAddressForm | '' | null | undefined) => {
      previousAddressInfoSnapshotRef.current =
        previousAddressInfoSnapshotRef.current ?? getAddressInfoSnapshot();
      setIsAddressMutationPending(true);
      setSuppressIncomingAddressSync(true);
      applyFormAddressInfo({
        address: (address || '') as unknown as IAddressForm,
        locationPoints: undefined,
      } as AddressInfoFormState);
    },
    [applyFormAddressInfo, getAddressInfoSnapshot],
  );

  const resetAddressMutationState = useCallback(() => {
    setPendingAddress(undefined);
    previousAddressInfoSnapshotRef.current = undefined;
    setIsAddressMutationPending(false);
    setSuppressIncomingAddressSync(false);
  }, []);

  const restorePreviousAddressInfo = useCallback(() => {
    if (previousAddressInfoSnapshotRef.current !== undefined) {
      applyFormAddressInfo(previousAddressInfoSnapshotRef.current);
    }
  }, [applyFormAddressInfo]);

  const onUpdateAddressInfo = (
    closeReason?: string,
    overrideAddress?: IAddressForm | null | undefined,
  ) => {
    const rawAddress = getValues('addressInfo.address') ?? overrideAddress;
    const newAddress =
      rawAddress && typeof rawAddress === 'object' ? rawAddress : undefined;
    setIsAddressMutationPending(true);
    setSuppressIncomingAddressSync(true);
    CfsApi.updateAddressInfo(cfsId, newAddress?._id, {
      businessName: newAddress?.businessName ?? null,
    })
      .then(async (res) => {
        const nextAddressInfo = {
          ...cfsDefaultValues.addressInfo,
          ...res.data.addressInfo,
          address: newAddress || undefined,
          locationPoints: res.data.addressInfo?.locationPoints,
        };

        applyFormAddressInfo(nextAddressInfo as any);
        ablyCfsChannel?.publish('cfsForm', {
          addressInfo: nextAddressInfo,
        });
        if (cfs)
          updateCFS({
            ...cfs,
            addressInfo: res.data.addressInfo,
            ...(!!closeReason && { closeReason, isClosed: true }),
          });

        await fetchCFS({ force: true });
        resetAddressMutationState();
      })
      .catch((err: any) => {
        restorePreviousAddressInfo();
        updateToast({ open: true, message: err.message });
        resetAddressMutationState();
      })
      .finally(() => {
        setIsAddressMutationPending(false);
        setSuppressIncomingAddressSync(false);
      });
    handleCloseAddressBoundaryModal();
  };

  const submitAgencyUpdate = (newAgencies: string[]) => {
    CfsApi.updateAgencies(cfsId, newAgencies)
      .then(async (res) => {
        ablyCfsChannel?.publish('cfsForm', {
          assignedAgencies: res.data.assignedAgencies,
        });
        if (cfs)
          updateCFS({
            ...cfs,
            assignedAgencies: res.data.assignedAgencies,
          });
        await Promise.all([
          fetchCFS({ force: true }),
          fetchAssignedUnitShifts(),
        ]);
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const onUpdateAgencies = (newAgencies: string[]) => {
    if (cfs) {
      submitAgencyUpdate(newAgencies);
    }
  };

  const handleAddressSelectionStart = (previewAddress: IAddressForm) => {
    setPendingAddress(previewAddress);
    stageAddressInfoMutation(previewAddress);
  };

  const handleAddressSelectionError = () => {
    restorePreviousAddressInfo();
    resetAddressMutationState();
  };

  const onPendingAddressUpdate = async (
    newValue: null | undefined | IAddressForm,
  ) => {
    if (!cfs) {
      return;
    }
    setPendingAddress(newValue);
    stageAddressInfoMutation(newValue);

    try {
      const checkResponse = await CfsApi.checkAgenciesUpdate(cfsId, {
        address: newValue?._id ?? undefined,
        incidentCodes: cfs.incidentCodes.map((ic) => String(ic._id ?? ic)),
      });

      setPendingRemovedAgencies(checkResponse.data);

      const affectedUnits = cfs.assignedUnitShifts.filter(
        (au) =>
          checkResponse.data
            .map((a) => a._id)
            .includes(au.unitShift.unit.agency) &&
          au.status !== AssignedUnitShiftStatus.CANCELED &&
          au.status !== AssignedUnitShiftStatus.COMPLETED,
      );

      if (checkResponse.data.length === 0 || affectedUnits.length === 0) {
        if (newValue) {
          if (
            account?.settings.areas &&
            newValue?.point &&
            getAddressCommonString(newValue as IAddress) !==
              getAddressCommonString(cfs?.addressInfo?.address) &&
            !isInsideMultiPolygon(newValue.point, account?.settings.areas)
          ) {
            setIsAddressMutationPending(false);
            return setOpenAddressBoundaryModal(true);
          }
          onUpdateAddressInfo();
        } else {
          onUpdateAddressInfo();
        }
        setPendingAddress(undefined);
      } else {
        setIsAddressMutationPending(false);
        setOpenAddressChangeModal(true);
      }
      return;
    } catch (err: any) {
      restorePreviousAddressInfo();
      resetAddressMutationState();
      updateToast({ open: true, message: err.message });
    }
  };

  const onUnassignIncidentCode = async (unassignedIncidentCode: string) => {
    if (!cfs) {
      return;
    }

    const cfsIncidentCodeIds = cfs.incidentCodes.map((ic) =>
      String(ic._id ?? ic),
    );

    setRemovingIncidentCode(unassignedIncidentCode);

    const newIncidentCodeIds = cfsIncidentCodeIds.filter(
      (ic) => ic !== unassignedIncidentCode,
    );

    const checkResponse = await CfsApi.checkAgenciesUpdate(cfsId, {
      address: cfs.addressInfo?.address?._id,
      incidentCodes: newIncidentCodeIds,
    });

    setPendingRemovedAgencies(checkResponse.data);

    const affectedUnits = cfs.assignedUnitShifts.filter(
      (au) =>
        checkResponse.data
          .map((a) => a._id)
          .includes(au.unitShift.unit.agency) &&
        au.status !== AssignedUnitShiftStatus.CANCELED &&
        au.status !== AssignedUnitShiftStatus.COMPLETED,
    );

    if (checkResponse.data.length === 0 || affectedUnits.length === 0) {
      onUpdateIncidentCodes(newIncidentCodeIds);
      clearUnassignIncidentCode();
    } else {
      setOpenIncidentCodeUnassignModal(true);
    }
  };

  const onUnassignAgency = async (unassignedAgency: string) => {
    setRemovingAgency(unassignedAgency);

    const affectedUnits = cfs?.assignedUnitShifts.filter(
      (au) =>
        au?.unitShift?.unit?.agency === unassignedAgency &&
        au.status !== AssignedUnitShiftStatus.COMPLETED &&
        au.status !== AssignedUnitShiftStatus.CANCELED,
    );

    if (affectedUnits && affectedUnits.length > 0) {
      setOpenAgencyUnassignModal(true);
    } else if (cfs?.assignedAgencies) {
      submitAgencyUpdate(
        cfs.assignedAgencies.filter((a) => a !== unassignedAgency),
      );
      clearUnassignAgency();
    }
  };

  const onUpdateIncidentCodes = (newIncidentCodes: string[]) => {
    const validCodes = newIncidentCodes.reduce((acc, item) => {
      const valid = incidentCodes.find(
        (code) => code._id === item || code.code === item,
      );
      if (valid?._id) {
        acc.push(valid._id);
      }
      return acc;
    }, [] as string[]);

    CfsApi.updateIncidentCodes(cfsId, validCodes)
      .then(async (res) => {
        setValue(
          'incidentCodes',
          (res.data.incidentCodes || []).map((ic) => String(ic._id ?? ic)),
        );
        ablyCfsChannel?.publish('cfsForm', {
          incidentCodes: res.data.incidentCodes,
          requiredUnits: res.data.requiredUnits,
        });
        updateCFS(res.data);
        await Promise.all([
          fetchCFS({ force: true }),
          fetchAssignedUnitShifts(),
        ]);
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const onUpdateCFS = (data: Partial<ICFSForm>) => {
    CfsApi.update(cfsId, data)
      .then((res) => {
        updateCFS((prev) => (prev ? { ...prev, ...res.data } : prev));
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const clearUnassignIncidentCode = () => {
    setRemovingIncidentCode(undefined);
    setPendingRemovedAgencies([]);
    setOpenIncidentCodeUnassignModal(false);
  };

  const clearUnassignAgency = () => {
    setRemovingAgency(undefined);
    setOpenAgencyUnassignModal(false);
  };

  const clearAddressChange = () => {
    setPendingAddress(undefined);
    setOpenAddressChangeModal(false);
  };

  const handleCancelAddressChange = () => {
    restorePreviousAddressInfo();
    resetAddressMutationState();
    clearAddressChange();
  };

  const handleCancelIncidentCodeUnassign = () => {
    clearUnassignIncidentCode();
  };

  const handleCancelAgencyUnassign = () => {
    clearUnassignAgency();
  };

  const handleAddressChange = () => {
    setOpenAddressChangeModal(false);
    if (pendingAddress) {
      setValue('addressInfo.address', pendingAddress);
      onUpdateAddressInfo();
    } else {
      setValue('addressInfo.address', '' as unknown as IAddressForm);
      onUpdateAddressInfo();
    }
  };

  const handleIncidentCodeUnassign = () => {
    if (removingIncidentCode && cfs?.incidentCodes) {
      onUpdateIncidentCodes(
        cfs.incidentCodes
          .filter((ic) => ic._id !== removingIncidentCode)
          .map((ic) => ic._id),
      );
      clearUnassignIncidentCode();
    }
  };

  const handleAgencyUnassign = () => {
    if (removingAgency && cfs?.assignedAgencies) {
      submitAgencyUpdate(
        cfs.assignedAgencies.filter((a) => a !== removingAgency),
      );
      clearUnassignAgency();
    }
  };

  const handleChange = async (fieldName: string, newValue: FormValueType) => {
    if (readOnly) return;
    const data = compositeObject(fieldName, newValue);

    if (fieldName === 'addressInfo.address') {
      const newAddress = getValues('addressInfo.address');
      if (
        account?.settings.areas &&
        newAddress?.point &&
        getAddressCommonString(newAddress as IAddress) !==
          getAddressCommonString(cfs?.addressInfo?.address) &&
        !isInsideMultiPolygon(newAddress.point, account?.settings.areas)
      ) {
        return setOpenAddressBoundaryModal(true);
      }
      return onUpdateAddressInfo();
    } else if (fieldName === 'incidentCodes') {
      return onUpdateIncidentCodes(newValue as string[]);
    } else if (fieldName === 'assignedAgencies') {
      return onUpdateAgencies(newValue as string[]);
    } else if (fieldName === 'reporter.contactPhoneNumber') {
      const isValidPhoneNumber = await trigger(
        fieldName as keyof Partial<ICFSForm>,
      );
      if (
        !isValidPhoneNumber ||
        `+1${newValue}` === cfs?.reporter.contactPhoneNumber
      ) {
        return;
      }

      const phoneData = !newValue
        ? { reporter: { contactPhoneNumber: undefined } }
        : compositeObject(fieldName, `+1${newValue}`);
      onUpdateCFS(phoneData);
      if (cfs) {
        updateCFS({
          ...cfs,
          reporter: {
            ...cfs.reporter,
            contactPhoneNumber: newValue ? `+1${newValue}` : undefined,
          },
        });
      }
      return;
    } else if (!OnBlurFormFields.includes(fieldName)) {
      onUpdateCFS(data);
    } else if (fieldName === 'addressInfo.instructions') {
      const address = getValues('addressInfo.address');
      // Spread the full current addressInfo so co-editing windows keep
      // sibling fields (businessName, locationPoints) intact.
      data.addressInfo = { ...cfs?.addressInfo, ...data.addressInfo, address };
    }
    ablyCfsChannel?.publish('cfsForm', data);
  };

  // Submit CFS form at onBlur event
  const onBlur = async (event: FocusEvent<HTMLFormElement>) => {
    if (!OnBlurFormFields.includes(event.target.name)) return;
    const fieldName = event.target.name;
    const value = event.target.value;

    let currentValue = '';
    if (fieldName === 'shortDescription') {
      currentValue = cfs?.shortDescription || '';
    } else if (fieldName === 'addressInfo.instructions') {
      currentValue = cfs?.addressInfo?.instructions || '';
    }

    const normalizedValue =
      fieldName === 'shortDescription' && value.length > 0
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : value;

    if (normalizedValue === currentValue) return;

    if (fieldName === 'shortDescription' && normalizedValue !== value) {
      setValue('shortDescription', normalizedValue);
    }

    const isValid = await trigger(fieldName as keyof Partial<ICFSForm>);
    if (!isValid) return;
    const data = compositeObject(fieldName, normalizedValue);
    onUpdateCFS(data);
  };

  const handleCloseAddressBoundaryModal = () => {
    if (checkAddress) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('checkAddress');
      setSearchParams(newSearchParams);
    }
    setOpenAddressBoundaryModal(false);
  };

  const handleClearAddress = () => {
    // hacky way to clear address
    stageAddressInfoMutation('' as unknown as IAddressForm);
    setPendingAddress(undefined);
    onUpdateAddressInfo();
    handleCloseAddressBoundaryModal();
  };

  const handleChangeReporterTime = async (v: string) => {
    if (readOnly) return;
    try {
      setTimelineCreatedTime(v);
      ablyCfsChannel?.publish('cfsForm', { reportTime: v });
      if (cfs)
        updateCFS({ ...cfs, reporter: { ...cfs.reporter, reportTime: v } });
      await CfsApi.update(String(cfsId), {
        reporter: { reportTime: v },
      });
      // Force re-fetch CFS and timeline so persisted data is in sync.
      fetchCFS({ force: true });
      fetchTimelineCreatedTime();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useEffect(() => {
    if (!cfs?._id || !isBaseCfsRoute) return;
    fetchTimelineCreatedTime();
  }, [fetchTimelineCreatedTime, isBaseCfsRoute, cfs?._id]);

  const handleOrganizationPhoneNumber = async (pNumber: string) => {
    try {
      const filter = {
        phoneNumbers: {
          $elemMatch: {
            phoneNumber: pNumber,
          },
        },
        type: MasterNameType.ORGANIZATION,
      };

      const { data }: any = await MasterNameApi.getMasterNames({
        filter: JSON.stringify(filter),
        limit: 1,
      });

      setOrganizationName(data.results?.[0]?.name || '');
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useEffect(() => {
    if (cfs?.reporter?.callerPhoneNumber) {
      handleOrganizationPhoneNumber(cfs?.reporter.callerPhoneNumber);
    }
  }, [cfs?.reporter?.callerPhoneNumber]);

  // Fetch latest incident codes when component mounts
  useEffect(() => {
    const refreshIncidentCodes = async () => {
      try {
        const response = await DataApi.getIncidentCodes(account?._id);
        if (response.data) {
          updateData({ incidentCodes: response.data });
        }
      } catch (error) {
        updateToast({
          open: true,
          message: 'Failed to refresh incident codes',
        });
      }
    };
    refreshIncidentCodes();
  }, [account?._id, updateData]);

  useEffect(() => {
    if (!cfs?.assignedUnitShifts || !cfs?.assignedAgencies) return;
    const assignedAgencySet = new Set(cfs.assignedAgencies.map(String));
    const allAgencyIdsFromUnits = Array.from(
      new Set(
        cfs.assignedUnitShifts
          ?.filter(
            (au) =>
              au.status !== AssignedUnitShiftStatus.CANCELED &&
              au.status !== AssignedUnitShiftStatus.COMPLETED,
          )
          .map((au) => String(au.unitShift?.unit?.agency)),
      ),
    );
    const missingAgencyIds = allAgencyIdsFromUnits.filter(
      (agencyId) => !assignedAgencySet.has(agencyId),
    );
    if (missingAgencyIds.length > 0) {
      const updatedAgencies = [...cfs.assignedAgencies, ...missingAgencyIds];
      setValue('assignedAgencies', updatedAgencies);
    } else {
      setValue('assignedAgencies', cfs.assignedAgencies);
    }
  }, [cfs?.assignedUnitShifts, cfs?.assignedAgencies]);

  const getFormFieldItems = (fieldName: string): SelectItem[] | undefined => {
    if (fieldName === 'incidentCodes') {
      return incidentCodeOptions;
    }

    if (fieldName === 'assignedAgencies') {
      return agencyOptions;
    }

    if (fieldName === 'severity') {
      return severityOptions;
    }

    return undefined;
  };

  const getFormFieldOnChipDelete = (
    fieldName: string,
  ): ((v: string | number) => Promise<void>) | undefined => {
    if (fieldName === 'incidentCodes') {
      return (v) => onUnassignIncidentCode(v as string);
    } else if (fieldName === 'assignedAgencies') {
      return (v) => onUnassignAgency(v as string);
    }

    return undefined;
  };

  return (
    <>
      <FormRoot
        onBlur={readOnly ? undefined : onBlur}
        sx={{ mt: '2px', pb: 0 }}
      >
        {CFSFormFields.map(({ title, name, items }) => (
          <FormCard
            key={title}
            sx={{
              '& .cardTitle': {
                padding: '5px 15px',
                minHeight: '50px',
                lineHeight: '40px',
              },
              '& .divider': {
                marginTop: 0,
              },
            }}
            title={
              name === 'addressInfo' &&
              (!!addressId || isAddressMutationPending) ? (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  className="cardTitle"
                >
                  <Typography variant="inherit" sx={{ fontWeight: 500 }}>
                    {title}
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    sx={{
                      px: 0.5,
                      py: 0.25,
                      borderRadius: '10px',
                      backgroundColor: 'background.formSubHeader',
                      '& > *': {
                        position: 'static !important',
                      },
                      '& > .address-action': {
                        display: 'flex',
                        alignItems: 'center',
                        height: '36px',
                      },
                      '& > .address-action + .address-action': {
                        marginLeft: 1,
                        paddingLeft: 1,
                        borderLeft: `1px solid ${colors.grey[60]}`,
                      },
                    }}
                  >
                    {isAddressMutationPending && (
                      <Box className="address-action">
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                        >
                          <CircularProgress size={14} thickness={6} />
                          <Typography variant="caption">Updating...</Typography>
                        </Stack>
                      </Box>
                    )}
                    {!!addressId && (
                      <>
                        {!readOnly && (
                          <Box className="address-action">
                            <CFSHistoryModal
                              addressId={addressId}
                              currentCfsId={cfsId}
                            />
                          </Box>
                        )}
                        <AddressPreplanModal addressId={addressId} />
                        {!readOnly && (
                          <Box className="address-action">
                            <AddressNotesModal addressId={addressId} />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                title
              )
            }
          >
            {items.map(({ styles, ...rest }) => (
              <Grid
                key={`${name}-${rest.name}`}
                {...{
                  ...styles,
                  marginRight:
                    rest.name === 'address' && isNotJurisdictionAddress ? 5 : 0,
                }}
                item
              >
                <RenderFormField
                  {...rest}
                  name={name ? `${name}.${rest.name}` : rest.name}
                  disabled={
                    readOnly ||
                    (rest.name === 'address'
                      ? isAddressMutationPending ||
                        Boolean((rest as any).disabled)
                      : (rest as any).disabled)
                  }
                  items={getFormFieldItems(rest.name)}
                  emptyContent={
                    rest.name === 'assignedAgencies' ? (
                      <Stack
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '5px',
                        }}
                      >
                        <IcInfoCircle
                          style={{
                            marginRight: 6,
                            color: colors.error.main,
                          }}
                        />{' '}
                        <span>Unable to Dispatch, No Assigned Agencies</span>
                      </Stack>
                    ) : undefined
                  }
                  locationRestriction={
                    rest.name === 'address' ? mapBounds : undefined
                  }
                  onChipDelete={
                    readOnly ? undefined : getFormFieldOnChipDelete(rest.name)
                  }
                  pendingAddress={pendingAddress}
                  showMileMarkerOption={true}
                  overrideUpdateAddress={
                    rest.name === 'address'
                      ? (addr) => onPendingAddressUpdate(addr)
                      : undefined
                  }
                  onAddressSelectionStart={
                    rest.name === 'address'
                      ? handleAddressSelectionStart
                      : undefined
                  }
                  onAddressSelectionError={
                    rest.name === 'address'
                      ? handleAddressSelectionError
                      : undefined
                  }
                  handleChange={handleChange}
                />
                {rest.name === 'address' && isNotJurisdictionAddress && (
                  <Tooltip
                    title="The selected address falls outside our jurisdiction"
                    arrow
                    followCursor
                  >
                    <Box
                      sx={{
                        width: 8,
                        pt: 3.6,
                        pl: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <IcTriangleWarning />
                    </Box>
                  </Tooltip>
                )}
              </Grid>
            ))}
          </FormCard>
        ))}
        <CallerInformation
          showRapidSOSIcon={!!locations?.length}
          callerInformation={cfs?.reporter?.callerPhoneNumber}
          reportTime={timelineCreatedTime || cfs?.reporter.reportTime}
          handleChangeReporterTime={handleChangeReporterTime}
          organizationName={organizationName}
          calls={cfs?.calls}
          readOnly={readOnly}
        />
        {!props.hideReporterInfo && (
          <ReporterForm cfsId={cfsId} onChange={handleChange} />
        )}
      </FormRoot>
      <CFSAddressJurisdictionModal
        open={openAddressBoundaryModal}
        onClose={onUpdateAddressInfo}
        onClearAddress={handleClearAddress}
        onKeepAddress={() => onUpdateAddressInfo()}
      />

      <CFSAgencyUnassignModal
        open={openAgencyUnassignModal}
        agencyId={removingAgency ?? ''}
        onClose={handleCancelAgencyUnassign}
        onSubmit={handleAgencyUnassign}
      />

      <CFSIncidentCodeUnassignModal
        open={openIncidentCodeUnassignModal}
        removedAgencies={pendingRemovedAgencies}
        onClose={handleCancelIncidentCodeUnassign}
        onSubmit={handleIncidentCodeUnassign}
      />

      <CFSAddressChangeModal
        open={openAddressChangeModal}
        isRemovingAddress={!!pendingAddress}
        removedAgencies={pendingRemovedAgencies}
        onClose={handleCancelAddressChange}
        onSubmit={handleAddressChange}
      />
    </>
  );
};
